/**
 * Consistent person label across dashboards, tables, approvals, exceptions, etc.
 *
 * Canonical staff identifier: Employee.employeeCode (surfaced on user as employeeCode).
 * Format: "First Last (EMP-####)" optionally with " · ROLE"
 */

export function personFullName(user) {
    if (!user) return '—';
    if (typeof user === 'string') return user;
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    return name || user.name || user.email || '—';
}

export function personRole(user) {
    if (!user) return '';
    return user.role || user.designation || user.roleLabel || '';
}

/** Canonical employee / user code — Employee.employeeCode (and nested employee fallbacks). */
export function personEmployeeCode(user) {
    if (!user) return '';
    return (
        user.employeeCode
        || user.employee?.employeeCode
        || user.employee?.code
        || ''
    );
}

/**
 * @param {object|string} user
 * @param {{ withRole?: boolean, withEmployeeCode?: boolean, short?: boolean }} [opts]
 */
export function formatUserLabel(user, opts = {}) {
    const { withRole = true, withEmployeeCode = true, short = false } = opts;
    const name = personFullName(user);
    if (!user || typeof user === 'string') return name;

    const code = withEmployeeCode ? personEmployeeCode(user) : '';
    const primary = code ? `${name} (${code})` : name;

    if (withRole) {
        const role = personRole(user);
        if (role) return short ? `${primary} · ${String(role).slice(0, 12)}` : `${primary} · ${role}`;
    }
    return primary;
}

/** Compact secondary line under a primary name (role). */
export function formatUserMeta(user) {
    if (!user || typeof user === 'string') return '';
    return personRole(user) || '';
}
